import { DistributedStoreClient } from "../distributedStoreClient";
import { FinsembleWindow } from "../../common/FinsembleWindow";
import { Logger } from "../logger";
import { System } from "../../common/system";
import { WindowIdentifier } from "../../platform/services/window/types";
import { getMyWindowIdentifier } from "../../common/util";
import { StandardError } from "../../types";
import { ResponderMessage } from "../routerClient";
import { StoreModel } from "../StoreModel";
import { Globals } from "../../common/Globals";
import SystemManagerClient from "../../common/systemManagerClient";
import { series as asyncSeries } from "async";

type Dialogs = {
	opened: any;
	available: any;
};

export class DialogManagerClientController {
	initialized: boolean = false;

	/**
	 * Can be used to set a specific timeout
	 */
	userInputTimeout: number | null = null;

	/**
	 * A global store used to track which windows are registered as dialogs and their state.
	 * Dialogs are put in the "available" pool when they are available for use. They are put
	 * in the "open" pool when they are in use.
	 */
	DialogStore: StoreModel | null = null;

	/**
	 * Will be set to true if the current window is a dialog
	 */
	isDialog: boolean = false;

	/**
	 * Contains a unique channel name that a dialog can use to communicate back to
	 * its originating window. The originating window creates the channel as
	 * a query responder.
	 */
	RESPONDER_CHANNEL: string | null = null;

	/**
	 * Used to send a message back to whoever opened this dialog
	 */
	openerMessage: ResponderMessage | null = null;

	/**
	 * Same as FEAGlobals.finsembleWindow
	 */
	finsembleWindow: FinsembleWindow | null = null;

	/**
	 * Will be set with the spawn data, which contains the input params and responseChannel
	 */
	spawnData: any = {};

	/**
	 * Checks whether the window is a dialog, setting this.isDialog to true or false
	 * @param {cb} cb The callback to be invoked after the method completes successfully.
	 */
	async checkIfWindowIsDialog(cb?: (err?: StandardError) => void): Promise<void> {
		if (Globals.finsembleWindow) {
			const { err, data: options } = await Globals.finsembleWindow.getOptions();
			this.spawnData = options?.customData?.spawnData ?? {};
			this.isDialog = options?.customData?.foreign?.clients?.dialogManager?.isDialog;
			cb?.(err);
		} else {
			this.isDialog = false;
			cb?.("Freestanding apps cannot be dialogs");
		}
	}

	/**
	 * Registers the window as a dialog with the global store. If the component is incapable of being used as a dialog (this is set in the component's config), the callback is immediately invoked.
	 *
	 * **Note:** This method checks whether the calling window is a dialog.
	 * @param {function} callback The callback to be invoked after the method completes successfully.
	 */
	async registerWithStore(cb?: () => void): Promise<void> {
		if (this.isDialog) {
			const myWindowIdentifier = await getMyWindowIdentifier();

			this.RESPONDER_CHANNEL = `Dialog.${myWindowIdentifier.windowName}.Show`;

			await this.DialogStore?.set(["dialogs", "available", myWindowIdentifier.windowName], myWindowIdentifier);
		}
		cb?.();
	}

	/**
	 * Establishes a connection to the distributed store that is used
	 * by all dialogs. This function may be called many times but will
	 * only initialize one time.
	 */
	initializeOnce() {
		return new Promise<void>(async (resolve) => {
			// Only initialize once
			if (this.initialized) {
				resolve();
				return;
			}
			await SystemManagerClient.waitForStartup("windowService");
			await this.checkIfWindowIsDialog();
			asyncSeries(
				[
					this.createStore.bind(this),
					this.registerWithStore.bind(this),
					this.getFinsembleWindow.bind(this),
					(done: () => void) => {
						done();
					},
				],
				() => {
					this.initialized = true;
					resolve();
				}
			);
		});
	}

	/**
	 * Creates the global store if it doesn't exist.
	 * @param {function} callback
	 */
	async createStore(cb?: () => void): Promise<void> {
		const defaults = {
			dialogs: {
				opened: {},
				available: {},
			},
		};
		const { err, data } = await DistributedStoreClient.createStore({
			store: "DialogStore",
			values: defaults,
			global: true,
		});
		if (err || !data) {
			Logger.error(`DialogManagerClient could not create store "DialogStore"`, err);
		} else {
			this.DialogStore = data;
		}
		cb?.();
	}

	/**
	 */
	async getFinsembleWindow(cb?: () => void): Promise<void> {
		// this.finsembleWindow must only be initialized in actual dialogs. Otherwise it causes side effects in services.
		if (this.isDialog) {
			const { wrap } = await FinsembleWindow.getInstance({ name: System.Window.getCurrent().name });
			this.finsembleWindow = wrap;
		}
		cb?.();
	}

	async getAvailableDialog(
		type: string,
		cb?: (dialog: WindowIdentifier | null) => void
	): Promise<WindowIdentifier | null> {
		if (!this.DialogStore) {
			const error = `DialogMangerClient.getAvailableDialogs() store not yet initialized}`;
			Logger.system.error(error);
			cb?.(null);
			return null;
		}
		const { err, data: availableDialogs } = await this.DialogStore.getGlobal<Record<string, WindowIdentifier>>([
			"dialogs",
			"available",
		]);
		if (err) {
			const error = `DialogMangerClient.getAvailableDialogs() store error ${err}`;
			Logger.system.error(error);
			cb?.(null);
			return null;
		}
		for (const dialogName in availableDialogs) {
			const dialog = availableDialogs[dialogName];
			if (dialog.componentType === type) {
				cb?.(dialog);
				return dialog;
			}
		}
		cb?.(null);
		return null;
	}

	/**
	 * State management - just moves an opened dialog back to the "available" pool.
	 * @param {object} identifier window identifier of the dialog.
	 */
	async moveDialogFromOpenedToAvailable(identifier: WindowIdentifier): Promise<string | null> {
		if (!this.DialogStore) {
			const error = `DialogMangerClient.moveDialogFromOpenedToAvailable() store not yet initialized}`;
			Logger.system.error(error);
			return error;
		}
		const { err, data: dialogs } = await this.DialogStore.getGlobal<Dialogs>(["dialogs"]);
		if (err || !dialogs) {
			const error = `DialogManagerClient.moveDialogFromOpenedToAvailable() no "dialogs" object returned from store`;
			Logger.system.error(error);
			return error;
		}
		const openedDialogs = dialogs.opened;
		const availableDialogs = dialogs.available;

		delete openedDialogs[identifier.windowName];
		availableDialogs[identifier.windowName] = identifier;

		await this.DialogStore?.set(["dialogs", "opened"], openedDialogs);
		await this.DialogStore?.set(["dialogs", "available"], availableDialogs);
		return null;
	}

	/**
	 * State management - just moves an available dialog out of the "available" pool.
	 * @param {object} identifier window identifier of the dialog.
	 */
	async moveDialogFromAvailableToOpened(identifier: WindowIdentifier): Promise<string | null> {
		if (!this.DialogStore) {
			const error = `DialogMangerClient.moveDialogFromAvailableToOpened() store not yet initialized}`;
			Logger.system.error(error);
			return error;
		}
		const { err, data: dialogs } = await this.DialogStore.getGlobal<Dialogs>(["dialogs"]);
		if (err || !dialogs) {
			const error = `DialogManagerClient.moveDialogFromAvailableToOpened no "dialogs" object returned from store`;
			Logger.system.error(error);
			return error;
		}
		const availableDialogs = dialogs.available;
		const openedDialogs = dialogs.opened;

		delete availableDialogs[identifier.windowName];
		openedDialogs[identifier.windowName] = identifier;

		await this.DialogStore?.set(["dialogs", "available"], availableDialogs);
		await this.DialogStore?.set(["dialogs", "opened"], openedDialogs);
		return null;
	}
}
